import {
  normalizeStorefrontBlockProps,
  resolveStorefrontBlockContract,
} from './blockContract'
import {
  buildRuntimeCacheKey,
  isValidPluginId,
  stableStringify,
} from '@emporion/plugin-sdk/block-contract'
import type { BlockInstance } from './types'

export const STOREFRONT_RUNTIME_TIMEOUT_MS = 6_000
export const STOREFRONT_RUNTIME_CACHE_TTL_MS = 10_000
export const STOREFRONT_RUNTIME_ERROR_TTL_MS = 2_000

export const STOREFRONT_RUNTIME_HTML_KEY = '__emporionRuntimeHtml'
export const STOREFRONT_RUNTIME_ERROR_KEY = '__emporionRuntimeError'

export type RuntimeCacheEntry = {
  html: string | null
  error: string | null
  expiresAt: number
}

export type StorefrontRuntimeContext = {
  locale?: string
  pageId?: string
  pageSlug?: string
  siteId?: string
  themeMode?: string
}

export type StorefrontRuntimePayload = {
  pluginId: string
  blockType: string
  props: Record<string, unknown>
  runtimeMode: 'storefront-runtime'
  configVersion?: number
  context: StorefrontRuntimeContext
}

export type StorefrontRuntimeResponse = {
  success?: boolean
  data?: {
    html?: unknown
  }
  error?: unknown
}

export const sanitizeStorefrontRuntimeContext = (
  context: StorefrontRuntimeContext,
): StorefrontRuntimeContext => {
  const safe: StorefrontRuntimeContext = {}
  const take = (value: unknown, max: number) =>
    typeof value === 'string' && value.trim().length > 0 && value.trim().length <= max
      ? value.trim()
      : undefined

  const locale = take(context.locale, 32)
  const pageId = take(context.pageId, 120)
  const pageSlug = take(context.pageSlug, 180)
  const siteId = take(context.siteId, 120)
  const themeMode = take(context.themeMode, 40)

  if (locale) safe.locale = locale
  if (pageId) safe.pageId = pageId
  if (pageSlug) safe.pageSlug = pageSlug
  if (siteId) safe.siteId = siteId
  if (themeMode) safe.themeMode = themeMode

  return safe
}

export const buildStorefrontRuntimeCacheKey = (payload: StorefrontRuntimePayload): string =>
  buildRuntimeCacheKey(
    payload.pluginId,
    payload.blockType,
    payload.configVersion ?? 1,
    stableStringify(payload.props),
    stableStringify(payload.context),
  )

export const resolveStorefrontRuntimePayload = (
  block: BlockInstance,
  context: StorefrontRuntimeContext,
): { ok: true; payload: StorefrontRuntimePayload } | { ok: false; reason: string } => {
  const contract = resolveStorefrontBlockContract(block)
  const pluginId = contract.pluginId
  if (!pluginId) return { ok: false, reason: 'missing_plugin_id' }
  if (!isValidPluginId(pluginId)) return { ok: false, reason: 'invalid_plugin_id' }
  if (contract.runtimeMode !== 'storefront-runtime') {
    return { ok: false, reason: 'runtime_mode_not_storefront' }
  }

  const blockType = typeof block.type === 'string' ? block.type.trim() : ''
  if (!blockType) return { ok: false, reason: 'invalid_block_type' }
  const normalizedProps = normalizeStorefrontBlockProps(block.props)

  return {
    ok: true,
    payload: {
      pluginId,
      blockType,
      props: normalizedProps,
      runtimeMode: 'storefront-runtime',
      ...(contract.configVersion ? { configVersion: contract.configVersion } : {}),
      context: sanitizeStorefrontRuntimeContext(context),
    },
  }
}
